import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { Snackbar } from "@mui/joy";
import AudioRecorder from "../components/audio/AudioRecorder";
import { PurpleBtn } from "../../Common/Style";
import SaveExitBtn from "../SaveExitBtn";

import { useSpeakingQuestion } from "../hooks/useSpeakingQuestion.js";
import { useSpeakingRecording } from "../hooks/useSpeakingRecording.js";

import { getSpeakingConfig, getSpeakingGuidelines } from "../utils/speakingConfig.js";
import { getResponsiveStyles } from "../utils/layoutHelpers.js";
import { formatText } from "../utils/textHelpers.js";
import { useSpeakingSubmission } from "../hooks/useSpeakingSubmission.js";
import TransitionContainer from "../Common/TransitionContainer";

export default function SpeakingFull({
  ExamId,
  quest,
  questions,
  step,
  setStep,
  MockTestAttemptID,
  totalQuestions,
  commulative,
  setCommulative,
  questionsTime,
  startStopwatch,
  stopStopwatch,
  resetQuestionTimer,
  endTestAndTimeout,
  Exam,
}) {
  const [NetworkError, setNetworkError] = useState(false);
  const [clicked, setClicked] = useState(false);

  const audioRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const processedRecordingRef = useRef(null);

  const isLaptopTwo = useMediaQuery("(max-width:1000px)");
  const isLaptopOne = useMediaQuery("(max-width:1400px)");

  const memoizedStartStopwatch = useCallback(() => {
    startStopwatch();
  }, [startStopwatch]);

  const { questData, loading: Loading, imageLoading: ImageLoading, setImageLoading } = useSpeakingQuestion(
    quest, 
    memoizedStartStopwatch
  );
  const isInitialLoading = Loading && !questData;
  const isFetchingNext = Loading && !!questData;
  
  const recordingState = useSpeakingRecording();

  const isPteCore = Exam?.core || false;
  const config = getSpeakingConfig(quest?.SubCategory, isPteCore);
  const guidelines = getSpeakingGuidelines(isPteCore);
  const guidelineText = useMemo(() => guidelines[quest?.SubCategory], [guidelines, quest?.SubCategory]);
  const responsiveStyles = getResponsiveStyles(isLaptopOne, isLaptopTwo);

  const { handleNext, handleExit, handleQuestionSubmission } = useSpeakingSubmission({
    quest,
    questData,
    recordingState,
    questionsTime,
    commulative,
    setCommulative,
    MockTestAttemptID,
    ExamId,
    step,
    totalQuestions,
    setStep,
    setNetworkError,
    resetQuestionTimer,
    audioRef,
    setClicked,
    stopStopwatch,
    endTestAndTimeout
  });

  useEffect(() => {
    if (recordingState.recordedAudioFile && 
        recordingState.recordedAudioFile.size > 0 && 
        !recordingState.transcriptLoading && 
        !isSubmittingRef.current &&
        processedRecordingRef.current !== recordingState.recordedAudioFile) {
      
      isSubmittingRef.current = true;
      processedRecordingRef.current = recordingState.recordedAudioFile;
      
      // Fire-and-forget submission in background so UI advances immediately
      Promise.resolve(handleQuestionSubmission()).finally(() => {
        isSubmittingRef.current = false;
      });
      // Move to next question immediately (if not last)
      setStep((prev) => (prev < totalQuestions ? prev + 1 : prev));
    }
  }, [recordingState.recordedAudioFile, recordingState.transcriptLoading, totalQuestions, quest?.SubCategory, setStep, handleQuestionSubmission]);

  return (
    <div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        open={NetworkError}
        color="danger"
        onClose={() => setNetworkError(false)}
      >
        Network Error
      </Snackbar>

      <div style={responsiveStyles.container}>
        <div
          style={{
            backgroundColor: "white",
            width: "100%",
            borderRadius: "24px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: "100%",
              backgroundColor: "#e2e2ea",
              height: "13px",
              position: "absolute",
            }}
          ></div>
          <div
            style={{
              width: `${(step / totalQuestions) * 100}%`,
              backgroundColor: "#996cfe",
              height: "13px",
              position: "absolute",
            }}
          ></div>

          <TransitionContainer isInitialLoading={isInitialLoading} isFetchingNext={isFetchingNext}>
            <div>
              <h1
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  lineHeight: "32px",
                }}
              >
                {guidelineText}
              </h1>

              <div style={responsiveStyles.contentWrapper}>
                {quest?.SubCategory === "Describe Image" && (
                  <div style={responsiveStyles.imageContainer}>
                    {ImageLoading ? (
                      <div
                        style={{
                          width: "80%",
                          height: "90%",
                          background: "rgba(0,0,0,0.06)",
                          borderRadius: 8,
                        }}
                      />
                    ) : null}
                    <img
                      src={questData?.QuestionImage}
                      alt="img"
                      onLoad={() => setImageLoading(false)}
                      style={{
                        maxWidth: "80%",
                        maxHeight: "90%",
                        display: ImageLoading ? "none" : "block",
                      }}
                    />
                  </div>
                )}

                {questData?.QuestionImage && (quest?.SubCategory === "Re-tell Lecture" || quest?.SubCategory === "Summarize Group Discussion") && (
                  <div style={responsiveStyles.imageContainerWithOverflow}>
                    {ImageLoading ? (
                      <div
                        style={{
                          width: "80%",
                          height: "90%",
                          background: "rgba(0,0,0,0.06)",
                          borderRadius: 8,
                        }}
                      />
                    ) : null}
                    <img
                      src={questData?.QuestionImage}
                      alt="img"
                      onLoad={() => {
                        setImageLoading(false);
                      }}
                      style={{
                        maxWidth: "80%", 
                        maxHeight: "90%",
                        display: ImageLoading ? "hidden" : "block",
                      }}
                    />
                  </div>
                )}

                <div style={responsiveStyles.audioRecorderContainer}>
                  {questData && (
                    <AudioRecorder
                      callNext={handleNext}
                      setTranscript={recordingState.setTranscript}
                      clicked={clicked}
                      step={step}
                      Beginning={config.beginning}
                      Waiting={config.waiting}
                      RecordingTime={config.recordingTime}
                      audio={
                        config.hasAudio && questData?.AudioObjects?.[0]
                          ? questData.AudioObjects[0]?.audio_url || questData.AudioObjects[0]?.url
                          : null
                      }
                      MicAudioRef={audioRef}
                      setDisabledButton={recordingState.setDisabledButton}
                      setRecordedAudioFile={recordingState.setRecordedAudioFile}
                      handleRecordedBlob={recordingState.handleRecordedBlob}
                      setTranscriptLoading={recordingState.setTranscriptLoading}
                    />
                  )}
                </div>
              </div>

              {(quest?.SubCategory === "Read Aloud" || quest?.SubCategory === "Respond to a situation") && questData?.QuestionStatement && (
                <h5 style={{ fontSize: "18px", fontWeight: 400, lineHeight: "28px" }}>
                  {formatText(questData?.QuestionStatement)}
                </h5>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "45px",
                }}
              >
                <SaveExitBtn handleExit={handleExit} />
                <PurpleBtn onClick={handleNext} disabled={recordingState.disabledButton}>
                  Next
                </PurpleBtn>
              </div>
            </div>
          </TransitionContainer>
        </div>
      </div>
    </div>
  );
}